import { useSearchParams } from 'react-router-dom';
import { useList, useSession } from '@yingbaba/api';
import type { Member } from '@yingbaba/types';
import { ActionDialog, Avatar, Empty, Pagination, QueryState, Select } from '@yingbaba/ui';
import { date, datetime } from '@yingbaba/utils';

export type MemberInvitationCreated = {
  token: string;
  email: string;
  role: string;
  expires_at: string;
};
type Invitation = {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
};
const roles = [
  ['owner', '所有者'],
  ['admin', '管理员'],
  ['manager', '项目负责人'],
  ['member', '普通成员'],
  ['finance', '财务'],
  ['viewer', '只读成员'],
] as const;
const statuses: Record<string, string> = {
  pending: '待加入',
  accepted: '已接受',
  revoked: '已撤销',
  expired: '已过期',
};
export const teamRoleLabel = (value: string) => roles.find(([key]) => key === value)?.[1] || value;

export function TeamDirectory({
  role,
  active,
  canManage,
}: {
  role: string;
  active: boolean;
  canManage: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'invitations' && canManage ? 'invitations' : 'members';
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const query = params.get('q') || '';
  const selectedRole = params.get('role') || '';
  const status = params.get('invitation_status') || 'pending';
  const session = useSession();
  const members = useList<Member>(
    `/workspace/members?${new URLSearchParams({ page: String(page), page_size: '20', q: query, role: selectedRole })}`,
    tab === 'members',
  );
  const invitations = useList<Invitation>(
    `/organizations/invitations?${new URLSearchParams({ page: String(page), page_size: '20', status })}`,
    tab === 'invitations',
  );
  function update(values: Record<string, string>) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(values))
        value ? next.set(key, value) : next.delete(key);
      return next;
    });
  }
  const canEditMember = active && ['owner', 'admin'].includes(role);
  const q = tab === 'members' ? members : invitations;
  return (
    <section className="panel team-directory">
      <nav className="tabs" aria-label="团队目录">
        <button
          type="button"
          className={tab === 'members' ? 'active' : ''}
          aria-current={tab === 'members' ? 'page' : undefined}
          onClick={() => update({ tab: '', page: '' })}
        >
          团队成员
        </button>
        {canManage && (
          <button
            type="button"
            className={tab === 'invitations' ? 'active' : ''}
            aria-current={tab === 'invitations' ? 'page' : undefined}
            onClick={() => update({ tab: 'invitations', page: '' })}
          >
            成员邀请
          </button>
        )}
      </nav>
      {tab === 'members' ? (
        <form
          key={query}
          className="team-directory-filter"
          role="search"
          aria-label="查找团队成员"
          onSubmit={(event) => {
            event.preventDefault();
            update({
              q: String(new FormData(event.currentTarget).get('q') || '').trim(),
              page: '',
            });
          }}
        >
          <input
            className="input"
            aria-label="搜索成员姓名或邮箱"
            name="q"
            defaultValue={query}
            placeholder="搜索姓名或邮箱"
            maxLength={200}
          />
          <Select
            aria-label="成员角色筛选"
            value={selectedRole}
            onChange={(value) => update({ role: value, page: '' })}
          >
            <option value="">全部角色</option>
            {roles.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <button className="btn btn-secondary" type="submit">
            搜索
          </button>
        </form>
      ) : (
        <div className="team-invitation-filter">
          <Select
            aria-label="邀请状态"
            value={status}
            onChange={(value) => update({ invitation_status: value, page: '' })}
          >
            {Object.entries(statuses).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <p className="small muted">邀请码仅限指定邮箱使用。未加入前可撤销，过期后可重新邀请。</p>
        </div>
      )}
      <QueryState query={q}>
        {tab === 'members' ? (
          members.data?.items.length ? (
            members.data.items.map((member) => (
              <article className="team-member-card" key={member.id}>
                <Avatar name={member.name} src={member.avatar_url} size={40} />
                <div className="team-member-info">
                  <strong>
                    {member.name}
                    {member.account_id === session.data?.account.id && (
                      <span className="small muted">（我）</span>
                    )}
                  </strong>
                  <p>{member.email}</p>
                  <small className="muted">加入于 {date(member.created_at)}</small>
                </div>
                <span className="tag">{teamRoleLabel(member.role)}</span>
                {canEditMember &&
                  member.role !== 'owner' &&
                  member.account_id !== session.data?.account.id &&
                  (role === 'owner' || member.role !== 'admin') && (
                    <div className="row wrap team-member-actions">
                      <ActionDialog
                        title={`调整 ${member.name} 的角色`}
                        trigger="调整角色"
                        variant="secondary"
                        submitLabel="保存成员角色"
                        path={`/members/${member.id}/role`}
                        fields={[
                          {
                            name: 'role',
                            label: '新的组织角色',
                            value: member.role,
                            options: roles
                              .filter(
                                ([value]) =>
                                  value !== 'owner' && (role === 'owner' || value !== 'admin'),
                              )
                              .map(([value, label]) => ({ value, label })),
                          },
                          { name: 'reason', label: '变更原因', type: 'textarea', min: 2 },
                        ]}
                      />
                      <ActionDialog
                        title={`移除 ${member.name}`}
                        trigger="移除"
                        variant="ghost"
                        intent="destructive"
                        path={`/members/${member.id}/remove`}
                        description="该成员将失去当前组织及关联项目的访问权限，历史记录会保留。"
                        fields={[{ name: 'reason', label: '移除原因', type: 'textarea', min: 2 }]}
                        submitLabel="确认移除成员"
                      />
                    </div>
                  )}
              </article>
            ))
          ) : (
            <Empty title="没有找到成员" description="试试其他姓名、邮箱或角色。" />
          )
        ) : invitations.data?.items.length ? (
          invitations.data.items.map((invitation) => (
            <article className="team-invitation-card" key={invitation.id}>
              <div>
                <strong>{invitation.email}</strong>
                <p>
                  {teamRoleLabel(invitation.role)} ·{' '}
                  {statuses[invitation.status] || invitation.status}
                </p>
                <small className="muted">有效期至 {datetime(invitation.expires_at)}</small>
              </div>
              {active &&
                invitation.status === 'pending' &&
                (invitation.role !== 'finance' || role === 'owner' || role === 'admin') && (
                  <ActionDialog
                    title="撤销成员邀请"
                    trigger="撤销邀请"
                    variant="secondary"
                    intent="destructive"
                    path={`/organizations/invitations/${invitation.id}/revoke`}
                    submitLabel="确认撤销邀请"
                    description={`撤销后，${invitation.email} 将无法使用原邀请码加入组织。`}
                    fields={[]}
                  />
                )}
            </article>
          ))
        ) : (
          <Empty
            title={status === 'pending' ? '没有待加入的邀请' : `暂无${statuses[status] || ''}邀请`}
            description={
              status === 'pending'
                ? '从页面上方邀请成员，再将邀请码发送给对应邮箱的同事。'
                : '可以切换状态查看其他邀请。'
            }
          />
        )}
        <Pagination
          page={page}
          size={20}
          total={q.data?.total || 0}
          onPage={(next) => update({ page: String(next) })}
        />
      </QueryState>
    </section>
  );
}
